import { ReadonlyVec2, ReadonlyVec3, vec3, vec4 } from 'gl-matrix';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { AdaptiveSphere } from '../../../data/common/customs/AdaptiveSphere';
import { PlaneView } from '../../../view/views/PlaneView';
import { BrushCursorAction, BrushCursorActionState } from './BrushCursorAction';
import { View } from '~/engine/view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';

/**
 * {@link AdaptiveBrushCursorActionState} inherits from {@link BrushCursorActionState}.\
 * It stores the state of the active {@link AdaptiveBrushCursorAction}.\
 * {@link AdaptiveBrushCursorActionState} is constructed when the {@link AdaptiveBrushCursorAction} starts. And deleted when the {@link AdaptiveBrushCursorAction} finishes.\
 * {@link AdaptiveBrushCursorAction} starts when the user moves their cursor over a {@link PlaneView}.\
 * It finishes when the user moves their mouse out of the {@link PlaneView} or starts drawing.\
 * {@link AdaptiveBrushCursorActionState} is used to render the brush cursor onto the screen.
 */
export class AdaptiveBrushCursorActionState extends BrushCursorActionState {
    /**
     * Constructor that will create a new {@link AdaptiveBrushCursorActionState} object.\
     * It will also add the {@link AdaptiveSphere} to the {@link Environment} and update the {@link ViewManager} with the new {@link VolumeViewUpdateEvent}.
     * @param view {@link PlaneView} that the {@link AdaptiveBrushCursorAction} is active on.
     * @param cursorObject {@link AdaptiveSphere} that will be rendered onto the screen.
     */
    public constructor(view: PlaneView, cursorObject: AdaptiveSphere) {
        super(view, cursorObject);
        view.addCustomObject(cursorObject);
        view.renderLayers();
        this._adaptiveSphere = cursorObject;
    }

    private readonly _adaptiveSphere: AdaptiveSphere;

    /**
     * Method to update the position of the {@link AdaptiveSphere} and update the {@link ViewManager} with the new {@link VolumeViewUpdateEvent}.
     * @param mousePosition {@link ReadonlyVec2} that contains the current mouse position in client pixel coordinates. Values in between [0, pixelSize].
     */
    public setMousePosition(mousePosition?: ReadonlyVec2): void {
        if (mousePosition) {
            const patientCoordinates =
                this._planeView.clientToPatientCoordinates(mousePosition);
            this._adaptiveSphere.setPosition(patientCoordinates);
        }
        this._planeView.renderLayers();
    }

    /**
     * Method to update the brush size of the {@link AdaptiveSphere}.
     * @param brushSize The new brush size in millimeters. This is the diameter of the {@link AdaptiveSphere}.
     */
    public updateBrushSize(brushSize: number): void {
        this._adaptiveSphere.resize(brushSize);
        this._planeView.renderLayers();
    }

    /**
     * Method to deallocate the gl WebGL memory consumed by this {@link AdaptiveBrushCursorActionState}.\
     * It will remove the {@link AdaptiveSphere} from the {@link Environment} and update the {@link ViewManager} with the new {@link VolumeViewUpdateEvent}.
     * @returns True if the memory was successfully deallocated. False otherwise.
     */
    public clearMemory(): void {
        this._planeView.removeCustomObject(this._adaptiveSphere);
        this._planeView.renderLayers();
    }
}

/**
 * {@link AdaptiveBrushCursorAction} inherits from {@link BrushCursorAction} with {@link AdaptiveBrushCursorActionState}.\
 * It is responsible for creating a {@link AdaptiveBrushCursorActionState} when the {@link AdaptiveBrushCursorAction} starts.\
 * It is also responsible for updating the {@link AdaptiveBrushCursorActionState} when the user moves their mouse.\
 * {@link AdaptiveBrushCursorAction} is active when the user moves their cursor over a {@link PlaneView}.\
 * It is inactive when the user moves their mouse out of the {@link PlaneView} or starts drawing.\
 */
export class AdaptiveBrushCursorAction extends BrushCursorAction {
    /**
     * Constructor that will create a new {@link AdaptiveBrushCursorAction} object.
     * @param brushSize The initial brush size in millimeters. This is the diameter of the {@link AdaptiveSphere}.
     */
    public constructor(
        private readonly image: ImageObject,
        brushSize: number,
        private readonly _color: ReadonlyVec3 = vec3.fromValues(1, 0, 0)
    ) {
        super(brushSize);
    }

    private _state: AdaptiveBrushCursorActionState | undefined;

    public onMouseMove(
        _coordinates: ReadonlyVec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        if (this._state && this._state.view !== _view) {
            this._state.clearMemory();
            this._state = undefined;
        }
        if (!this._state) {
            this._state = this.startAction(_view as PlaneView, _coordinates);
        } else {
            this._state.setMousePosition(_coordinates);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Method to start the {@link AdaptiveBrushCursorAction}.\
     * It will create a new {@link AdaptiveBrushCursorActionState} and return it.\
     * {@link AdaptiveBrushCursorAction} requires an {@link ImageObject} to be present in the {@link Environment} in order to be activated.\
     * If no {@link ImageObject} is present, it will return undefined.
     * @param view {@link PlaneView} that the {@link AdaptiveBrushCursorAction} is active on.
     * @param coordinates {@link ReadonlyVec2} client pixel coordinates of the mouse position. Values in between [0, pixelSize].
     * @returns A new {@link AdaptiveBrushCursorActionState} object if an {@link ImageObject} is present in the {@link Environment}. Undefined otherwise.
     */
    protected startAction(
        view: PlaneView,
        coordinates: ReadonlyVec2
    ): AdaptiveBrushCursorActionState | undefined {
        view.snapToImagePixel();
        const patientCoordinates = view.clientToPatientCoordinates(coordinates);
        const adaptiveSphere = new AdaptiveSphere(
            this.image,
            patientCoordinates,
            this.brushSize,
            this.image.grid.resX,
            vec4.fromValues(this._color[0], this._color[1], this._color[2], 1)
        );
        return new AdaptiveBrushCursorActionState(view, adaptiveSphere);
    }

    /**
     * Method to update the {@link brushSize} of the {@link AdaptiveBrushCursorAction}.\
     * It will also update the {@link brushSize} of the active {@link AdaptiveBrushCursorActionState}, if present.
     * @param brushSize The new brush size in millimeters. This is the diameter of the {@link AdaptiveSphere}.
     */
    public updateBrushSize(brushSize: number): void {
        this.brushSize = brushSize;
        if (this._state) {
            this._state.updateBrushSize(brushSize);
        }
    }

    public stopAction(): EventHandlerResult {
        if (this._state) {
            this._state.clearMemory();
            this._state = undefined;
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }
}
